import React from 'react';

import { Icon } from 'ui';
import { useId } from 'ui/utils';

type Props = {
  id?: string;
  name?: string;
  required?: boolean;
  value?: string;
  rows?: number;
  icon?: React.ComponentProps<typeof Icon>['name'];
  placeholder?: string;
  autoFocus?: boolean;
  error?: string;
  onChange: (newValue: string) => void;
};

function Textarea({
  id,
  name,
  icon,
  required = false,
  value = '',
  rows = 3,
  placeholder,
  autoFocus,
  error,
  onChange,
}: Props) {
  const internalId = useId();

  if (!name) {
    name = `textarea-${internalId}`;
  }

  if (!id) {
    id = `${name}__id`;
  }

  const helpTextId = `${id}__description`;

  const inputClasses = [
    'shadow-sm',
    'block',
    'w-full',
    'sm:text-sm',
    'rounded-md',
    'placeholder-gray-300',
    'text-gray-900',
  ];

  if (error) {
    inputClasses.push(
      'border-red-300',
      'text-red-900',
      'placeholder-red-300',
      'focus:outline-none',
      'focus:ring-red-500',
      'focus:border-red-500',
    );
  } else {
    inputClasses.push('focus:ring-indigo-500', 'focus:border-indigo-500', 'border-gray-300');
  }

  if (icon) {
    inputClasses.push('pl-10');
  }

  return (
    <div className="relative flex-grow">
      {icon && (
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <Icon name={icon} className="h-5 w-5 text-gray-400" />
        </div>
      )}
      <textarea
        autoFocus={autoFocus}
        name={name}
        id={id}
        placeholder={placeholder}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        aria-describedby={helpTextId}
        rows={rows}
        className={inputClasses.join(' ')}
      />
      {error && (
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          <svg
            className="h-5 w-5 text-red-500"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      )}
    </div>
  );
}

export default Textarea;
